import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/lib/api';
import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from '@/template/lib/toast';
import { Mail, MoreHorizontal, Eye, CheckCircle, Clock, MessageSquare } from 'lucide-react';
import type { Id } from 'convex/_generated/dataModel';
import { type ContactInquiryStatus } from '@/types/validators';
import { type ContactInquiry, type ContactStatusFilter } from '@/types/contact';
import { formatBerlinDateTime } from '@/lib/berlin-time';
import { useTranslation } from 'react-i18next';

const ContactInquiriesPage = () => {
  const { t, i18n } = useTranslation('operator');
  const [statusFilter, setStatusFilter] = useState<ContactStatusFilter>('all');

  // Query contact inquiries
  const data = useQuery(api.app.contact.listContactInquiries, {
    status: statusFilter,
    limit: 50,
  });

  const updateStatus = useMutation(api.app.contact.updateContactInquiryStatus);

  const handleStatusChange = async (inquiryId: Id<'contactInquiries'>, newStatus: ContactInquiryStatus) => {
    try {
      await updateStatus({
        inquiryId,
        status: newStatus,
      });
      toast.success(t('contacts.toasts.statusChanged', { status: getStatusLabel(newStatus) }));
    } catch (error) {
      toast.error(t('contacts.toasts.statusChangeError'));
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'new':
        return t('contacts.status.new');
      case 'read':
        return t('contacts.status.read');
      case 'resolved':
        return t('contacts.status.resolved');
      default:
        return status;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return (
          <Badge className='bg-primary/10 text-primary border-primary/20'>
            <Clock className='size-3 mr-1' />
            {t('contacts.status.new')}
          </Badge>
        );
      case 'read':
        return (
          <Badge variant='secondary'>
            <Eye className='size-3 mr-1' />
            {t('contacts.status.read')}
          </Badge>
        );
      case 'resolved':
        return (
          <Badge className='bg-success/10 text-success border-success/20'>
            <CheckCircle className='size-3 mr-1' />
            {t('contacts.status.resolved')}
          </Badge>
        );
      default:
        return <Badge variant='outline'>{status}</Badge>;
    }
  };

  const formatDate = (timestamp: number) => {
    return formatBerlinDateTime(timestamp, i18n.language || 'de-DE');
  };

  const truncateMessage = (message: string, maxLength: number = 80) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const tabs: Array<{ key: ContactStatusFilter; label: string }> = [
    { key: 'all', label: t('contacts.filters.all') },
    { key: 'new', label: t('contacts.status.new') },
    { key: 'read', label: t('contacts.status.read') },
    { key: 'resolved', label: t('contacts.status.resolved') },
  ];

  const inquiries: ContactInquiry[] = (data?.inquiries ?? []) as ContactInquiry[];
  const isLoading = data === undefined;

  return (
    <div className='p-4 sm:p-6 lg:p-8 space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-semibold flex items-center gap-2'>
            <Mail className='size-6' />
            {t('contacts.title')}
          </h1>
          <p className='text-sm text-muted-foreground mt-1'>{t('contacts.subtitle')}</p>
        </div>
      </div>

      {/* Content Card */}
      <Card>
        <CardHeader className='pb-3'>
          <div className='flex flex-wrap gap-2'>
            {tabs.map((tab) => (
              <Button key={tab.key} variant={statusFilter === tab.key ? 'default' : 'outline'} size='sm' onClick={() => setStatusFilter(tab.key)}>
                {tab.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-12'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
            </div>
          ) : inquiries.length === 0 ? (
            <div className='text-center py-12'>
              <MessageSquare className='size-12 mx-auto text-muted-foreground/50 mb-4' />
              <p className='text-muted-foreground'>{t('contacts.empty')}</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-[140px]'>{t('contacts.table.date')}</TableHead>
                    <TableHead className='w-[180px]'>{t('contacts.table.name')}</TableHead>
                    <TableHead className='w-[220px]'>{t('contacts.table.email')}</TableHead>
                    <TableHead>{t('contacts.table.message')}</TableHead>
                    <TableHead className='w-[120px]'>{t('contacts.table.status')}</TableHead>
                    <TableHead className='w-[60px]' />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.map((inquiry) => (
                    <TableRow key={inquiry._id} className={inquiry.status === 'new' ? 'bg-primary/5' : ''}>
                      <TableCell className='text-sm text-muted-foreground'>{formatDate(inquiry.createdAt)}</TableCell>
                      <TableCell className='font-medium'>{inquiry.name}</TableCell>
                      <TableCell>
                        <a href={`mailto:${inquiry.email}`} className='text-primary hover:underline'>
                          {inquiry.email}
                        </a>
                      </TableCell>
                      <TableCell className='text-sm text-muted-foreground' title={inquiry.message}>
                        {truncateMessage(inquiry.message)}
                      </TableCell>
                      <TableCell>{getStatusBadge(inquiry.status)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger render={<Button variant='ghost' size='icon' className='h-8 w-8' />}>
                            <MoreHorizontal className='size-4' />
                            <span className='sr-only'>{t('contacts.table.actions')}</span>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='end'>
                            {inquiry.status !== 'new' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(inquiry._id, 'new')}>
                                <Clock className='size-4 mr-2' />
                                {t('contacts.actions.markNew')}
                              </DropdownMenuItem>
                            )}
                            {inquiry.status !== 'read' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(inquiry._id, 'read')}>
                                <Eye className='size-4 mr-2' />
                                {t('contacts.actions.markRead')}
                              </DropdownMenuItem>
                            )}
                            {inquiry.status !== 'resolved' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(inquiry._id, 'resolved')}>
                                <CheckCircle className='size-4 mr-2' />
                                {t('contacts.actions.markResolved')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination info */}
          {data?.nextCursor && (
            <div className='flex justify-center mt-4'>
              <Button variant='outline' size='sm' disabled>
                {t('contacts.actions.loadMore')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/contacts')({
  component: ContactInquiriesPage,
});
